import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import blackLoginPicture from '../../assets/blackLoginPicture.webp';
import whiteLoginPicture from '../../assets/whiteLoginPicture.webp';
import { useTheme } from '../../ThemeContext';
import { Modal, Button, CloseButton, Form } from 'react-bootstrap';
import { Check2Square, Square, ArrowRight } from 'react-bootstrap-icons'

const HANDSHAKE_STORAGE_KEY = 'salew:handshakeId';
const REFERRER_STORAGE_KEY = 'salew:pendingReferrerId';
const REF_CLAIM_STORAGE_KEY = 'salew:pendingRefClaimToken';
const EMAIL_PATTERN = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const LoginPage = () => {
  const { theme } = useTheme();
  const backgroundImage = theme === 'dark' ? blackLoginPicture : whiteLoginPicture;
  const navigate = useNavigate();
  const location = useLocation();
  const {
    startBotHandshake,
    checkBotHandshakeStatus,
    finalizeBotHandshake,
    sendEmailAuthCode,
    verifyEmailAuthCode,
    showModal,
    setShowModal,
  } = useAuth();

  const [isChecked, setIsChecked] = useState(false);
  const initialHandshakeId = useMemo(() => {
    if (typeof window === 'undefined') {
      return null;
    }
    try {
      return window.localStorage.getItem(HANDSHAKE_STORAGE_KEY);
    } catch (error) {
      console.warn('Не удалось прочитать данные авторизации из localStorage:', error);
      return null;
    }
  }, []);
  const [handshakeId, setHandshakeId] = useState(initialHandshakeId);
  const [handshakeStatus, setHandshakeStatus] = useState(initialHandshakeId ? 'pending' : null);
  const [isProcessing, setIsProcessing] = useState(Boolean(initialHandshakeId));
  const [errorMessage, setErrorMessage] = useState('');
  const [email, setEmail] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [emailStep, setEmailStep] = useState('email');
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [emailInfo, setEmailInfo] = useState('');
  const [pendingReferrerId, setPendingReferrerId] = useState(() => {
    if (typeof window === 'undefined') {
      return null;
    }
    const raw = window.localStorage.getItem(REFERRER_STORAGE_KEY);
    if (!raw) return null;
    const parsed = Number.parseInt(raw, 10);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  });
  const [pendingRefClaimToken, setPendingRefClaimToken] = useState(() => {
    if (typeof window === 'undefined') {
      return null;
    }
    const raw = window.localStorage.getItem(REF_CLAIM_STORAGE_KEY);
    if (!raw) return null;
    const normalized = String(raw).trim();
    return normalized || null;
  });
  const finalizingRef = useRef(false);

  const clearHandshakeState = useCallback((status = null) => {
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.removeItem(HANDSHAKE_STORAGE_KEY);
      } catch (error) {
        console.warn('Не удалось очистить данные авторизации из localStorage:', error);
      }
    }
    finalizingRef.current = false;
    setHandshakeId(null);
    setHandshakeStatus(status);
    setIsProcessing(false);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const candidates = [
      params.get('ref'),
      params.get('referrer_id'),
      params.get('start'),
    ].filter(Boolean);

    let parsedReferrer = null;
    for (const candidate of candidates) {
      const value = String(candidate);
      const numericMatch = value.match(/\d+/);
      if (!numericMatch) continue;
      const parsed = Number.parseInt(numericMatch[0], 10);
      if (Number.isInteger(parsed) && parsed > 0) {
        parsedReferrer = parsed;
        break;
      }
    }

    if (parsedReferrer) {
      setPendingReferrerId(parsedReferrer);
      try {
        window.localStorage.setItem(
          REFERRER_STORAGE_KEY,
          String(parsedReferrer)
        );
      } catch (error) {
        console.warn('Не удалось сохранить реферала в localStorage:', error);
      }
    }
  }, [location.search]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const claimFromQuery = (params.get('ref_claim') || '').trim();
    if (!claimFromQuery) {
      return;
    }

    setPendingRefClaimToken(claimFromQuery);
    try {
      window.localStorage.setItem(REF_CLAIM_STORAGE_KEY, claimFromQuery);
    } catch (error) {
      console.warn('Не удалось сохранить ref claim token в localStorage:', error);
    }
  }, [location.search]);

  const handleOpenBot = useCallback(async () => {
    if (!isChecked || isProcessing) {
      return;
    }

    try {
      setIsProcessing(true);
      setHandshakeStatus('pending');
      setErrorMessage('');
      finalizingRef.current = false;

      const generateHandshakeId = () => {
        if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
          return window.crypto.randomUUID().replace(/-/g, '');
        }
        return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`.slice(0, 48);
      };

      const newHandshakeId = generateHandshakeId();
      await startBotHandshake(newHandshakeId, { platform: navigator?.platform || 'unknown' });

      if (typeof window !== 'undefined') {
        try {
          window.localStorage.setItem(HANDSHAKE_STORAGE_KEY, newHandshakeId);
        } catch (error) {
          console.warn('Не удалось сохранить данные авторизации в localStorage:', error);
        }
      }

      setHandshakeId(newHandshakeId);
      setHandshakeStatus('pending');

      const telegramWebUrl = `https://t.me/salew_bot?start=auth_${newHandshakeId}`;
      const telegramAppUrl = `tg://resolve?domain=salew_bot&start=auth_${newHandshakeId}`;
      let fallbackTimer = null;

      function handleVisibilityChange() {
        if (document.visibilityState === 'hidden') {
          cleanupFallback();
        }
      }

      function cleanupFallback() {
        if (fallbackTimer) {
          clearTimeout(fallbackTimer);
          fallbackTimer = null;
        }
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      }

      window.location.href = telegramWebUrl;

      fallbackTimer = window.setTimeout(() => {
        if (!document.hidden) {
          window.location.href = telegramAppUrl;
        }
        cleanupFallback();
      }, 800);

      document.addEventListener('visibilitychange', handleVisibilityChange);
    } catch (error) {
      console.error('Ошибка запуска авторизации через бота:', error);
      setErrorMessage(error.message || 'Не удалось начать авторизацию через Telegram-бота');
      setIsProcessing(false);
    }
  }, [isChecked, isProcessing, startBotHandshake]);

  const handleSendEmailCode = useCallback(async () => {
    if (!isChecked || emailLoading) {
      return;
    }

    const normalizedEmail = email.trim().toLowerCase();
    if (!EMAIL_PATTERN.test(normalizedEmail)) {
      setEmailError('Введите корректный email');
      return;
    }

    setEmailLoading(true);
    setEmailError('');
    setEmailInfo('');

    try {
      const response = await sendEmailAuthCode(normalizedEmail);
      setEmail(normalizedEmail);
      setEmailStep('code');
      setEmailInfo(
        response?.detail ||
        'Код отправлен на email. Проверьте входящие и папку Спам.'
      );
    } catch (error) {
      setEmailError(error.message || 'Не удалось отправить код');
    } finally {
      setEmailLoading(false);
    }
  }, [email, isChecked, emailLoading, sendEmailAuthCode]);

  const handleVerifyEmailCode = useCallback(async () => {
    if (!isChecked || emailLoading) {
      return;
    }

    const normalizedEmail = email.trim().toLowerCase();
    const normalizedCode = emailCode.trim();
    if (!EMAIL_PATTERN.test(normalizedEmail)) {
      setEmailError('Введите корректный email');
      return;
    }
    if (!/^\d{6}$/.test(normalizedCode)) {
      setEmailError('Код должен состоять из 6 цифр');
      return;
    }

    setEmailLoading(true);
    setEmailError('');
    setEmailInfo('');

    try {
      const response = await verifyEmailAuthCode(
        normalizedEmail,
        normalizedCode,
        pendingReferrerId,
        pendingRefClaimToken
      );
      if (response?.token) {
        try {
          window.localStorage.removeItem(REFERRER_STORAGE_KEY);
          window.localStorage.removeItem(REF_CLAIM_STORAGE_KEY);
        } catch (error) {
          console.warn('Не удалось очистить сохраненного реферала:', error);
        }
        setPendingReferrerId(null);
        setPendingRefClaimToken(null);
        navigate('/');
        return;
      }
      setEmailError(response?.detail || 'Не удалось завершить вход');
    } catch (error) {
      setEmailError(error.message || 'Не удалось подтвердить код');
    } finally {
      setEmailLoading(false);
    }
  }, [
    email,
    emailCode,
    isChecked,
    emailLoading,
    verifyEmailAuthCode,
    pendingReferrerId,
    pendingRefClaimToken,
    navigate,
  ]);

  useEffect(() => {
    if (!handshakeId) {
      return;
    }

    let active = true;
    let timerId;

    const pollStatus = async () => {
      if (!active) {
        return;
      }

      try {
        const statusData = await checkBotHandshakeStatus(handshakeId);
        const status = statusData?.status;

        if (status) {
          setHandshakeStatus(status);
        }

        if (status === 'confirmed') {
          if (!finalizingRef.current) {
            finalizingRef.current = true;
            try {
              const finalizeData = await finalizeBotHandshake(handshakeId);
              if (finalizeData?.token) {
                active = false;
                clearHandshakeState('completed');
                setErrorMessage('');
                navigate('/');
                return;
              }

              if (finalizeData?.detail === 'handshake_pending') {
                finalizingRef.current = false;
              } else if (finalizeData?.detail) {
                setErrorMessage(finalizeData.detail);
              }
            } catch (finalizeError) {
              finalizingRef.current = false;
              console.error('Ошибка завершения авторизации через бота:', finalizeError);
              setErrorMessage(finalizeError.message || 'Не удалось завершить авторизацию через Telegram-бота');
            }
          }
        } else if (status === 'completed') {
          active = false;
          clearHandshakeState('completed');
          setErrorMessage('');
        } else if (status === 'expired' || status === 'not_found') {
          active = false;
          clearHandshakeState(status);
          setErrorMessage('Ссылка устарела или не найдена. Попробуйте запустить авторизацию заново.');
        } else if (status) {
          setErrorMessage('');
        }
      } catch (statusError) {
        console.error('Ошибка проверки статуса авторизации через бота:', statusError);
        setErrorMessage(statusError.message || 'Не удалось проверить статус авторизации через бота');
      } finally {
        if (active) {
          timerId = window.setTimeout(pollStatus, 5000);
        }
      }
    };

    pollStatus();

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        pollStatus();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      active = false;
      if (timerId) {
        clearTimeout(timerId);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      finalizingRef.current = false;
    };
  }, [handshakeId, checkBotHandshakeStatus, finalizeBotHandshake, clearHandshakeState, navigate]);

  const statusText = useMemo(() => {
    switch (handshakeStatus) {
      case 'pending':
        return 'Ожидаем подтверждение в Telegram. После завершения вернитесь в приложение.';
      case 'confirmed':
        return 'Подтверждение получено, завершаем авторизацию…';
      case 'completed':
        return 'Авторизация завершена. Открываем приложение…';
      case 'expired':
        return 'Ссылка устарела. Попробуйте начать авторизацию заново.';
      case 'not_found':
        return 'Сессия авторизации не найдена. Попробуйте ещё раз.';
      default:
        return null;
    }
  }, [handshakeStatus]);


  return (
    <div
      className="d-flex justify-content-center align-items-center"
      style={{
        minHeight: '100vh',
        backgroundImage: `url(${backgroundImage})`,
        backgroundPosition: 'center',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        position: 'relative',
      }}
    >
      <div
        className="position-absolute w-100 h-100"
        style={{
          backgroundColor: theme === 'dark' ? 'rgba(0,0,0,0.7)' : 'rgba(255,255,255,0.9)',
        }}
      />

      <div className="position-relative text-center p-5 rounded-4 shadow-lg"
        style={{
          backgroundColor: theme === 'dark' ? 'rgba(33,37,41,0.95)' : 'rgba(255,255,255,0.95)',
          maxWidth: '450px',
          width: '90%',
          border: theme === 'dark' ? '1px solid #495057' : '1px solid #dee2e6',
        }}>
        <h2 className="mb-4 fw-bold" style={{ color: theme === 'dark' ? '#fff' : '#212529' }}>
          Добро пожаловать
        </h2>

        <div className="d-flex flex-column gap-3 mb-4">
          <button
            onClick={() => setIsChecked(!isChecked)}
            className="d-flex align-items-center gap-2 bg-transparent border-0 p-0"
            style={{ color: theme === 'dark' ? '#fff' : '#212529' }}
          >
            {isChecked ? (
              <Check2Square size={57} className="text-primary" />
            ) : (
              <Square size={50} className="text-secondary" />
            )}
            <span className="text-start small">
              Я согласен с{' '}
              <a
                href="https://salew-bot.ru/agreement.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="text-decoration-none"
                style={{ color: '#0d6efd' }}
              >
                пользовательским соглашением
              </a>{' '}
              и{' '}
              <a
                href="https://salew-bot.ru/politika-obrabotki-personalnyh-dannyh.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="text-decoration-none"
                style={{ color: '#0d6efd' }}
              >
                Политикой обработки персональных данных
              </a>
            </span>
          </button>

          <div
            style={{
              position: 'relative',
              cursor: !isChecked || isProcessing ? 'not-allowed' : 'pointer'
            }}
          >
            <button
              type="button"
              onClick={handleOpenBot}
              className="btn btn-primary w-100 py-2 fw-bold"
              disabled={!isChecked || isProcessing}
            >
              {isProcessing ? 'Ожидаем подтверждение…' : 'Продолжить в Telegram'}
            </button>
          </div>
          {(statusText || errorMessage) && (
            <div className="text-start small">
              {statusText && (
                <p className="mb-1 text-muted">
                  {statusText}
                </p>
              )}
              {errorMessage && (
                <p className="mb-0 text-danger">
                  {errorMessage}
                </p>
              )}
            </div>
          )}

          <div
            className="pt-3 mt-1"
            style={{ borderTop: theme === 'dark' ? '1px solid #495057' : '1px solid #dee2e6' }}
          >
            <p className="text-start small mb-2" style={{ color: theme === 'dark' ? '#adb5bd' : '#495057' }}>
              Если у вас уже есть подписка в боте/приложении, сначала войдите через Telegram и добавьте email в настройках.
            </p>
            <p className="text-start small mb-3" style={{ color: theme === 'dark' ? '#868e96' : '#6c757d' }}>
              Код отправляется на почту и действует ограниченное время.
            </p>

            <Form.Group className="mb-2 text-start">
              <Form.Label className="small mb-1">Email</Form.Label>
              <Form.Control
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="name@example.com"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                disabled={emailLoading}
              />
            </Form.Group>

            {emailStep === 'code' && (
              <Form.Group className="mb-2 text-start">
                <Form.Label className="small mb-1">Код из письма</Form.Label>
                <Form.Control
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="6 цифр"
                  value={emailCode}
                  onChange={(event) => setEmailCode(event.target.value.replace(/[^\d]/g, '').slice(0, 6))}
                  disabled={emailLoading}
                />
              </Form.Group>
            )}

            <div className="d-flex gap-2 mt-2">
              {emailStep === 'email' ? (
                <Button
                  variant="outline-primary"
                  className="w-100 fw-semibold"
                  disabled={!isChecked || emailLoading}
                  onClick={handleSendEmailCode}
                >
                  {emailLoading ? 'Отправка…' : 'Войти по Email'}
                </Button>
              ) : (
                <>
                  <Button
                    variant="outline-secondary"
                    className="fw-semibold"
                    disabled={emailLoading}
                    onClick={() => {
                      setEmailStep('email');
                      setEmailCode('');
                      setEmailError('');
                      setEmailInfo('');
                    }}
                  >
                    Назад
                  </Button>
                  <Button
                    variant="primary"
                    className="w-100 fw-semibold"
                    disabled={!isChecked || emailLoading}
                    onClick={handleVerifyEmailCode}
                  >
                    {emailLoading ? 'Проверка…' : 'Подтвердить код'}
                  </Button>
                </>
              )}
            </div>

            {(emailInfo || emailError || pendingReferrerId || pendingRefClaimToken) && (
              <div className="text-start small mt-2">
                {pendingReferrerId && (
                  <p className="mb-1 text-muted">
                    Реферал сохранен: ID {pendingReferrerId}
                  </p>
                )}
                {pendingRefClaimToken && (
                  <p className="mb-1 text-muted">
                    Реферальная ссылка сохранена и применится после входа.
                  </p>
                )}
                {emailInfo && (
                  <p className="mb-1 text-muted">
                    {emailInfo}
                  </p>
                )}
                {emailError && (
                  <p className="mb-0 text-danger">
                    {emailError}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="mt-4 small">
          <p className="mb-0">
            📹{' '}
            <a
              href="https://salew-bot.ru/videos"
              target="_blank"
              rel="noopener noreferrer"
              className="text-decoration-none d-inline-flex align-items-center gap-1"
              style={{ color: '#0d6efd' }}
            >
              Видеоинструкция
            </a>
          </p>
        </div>
        <div className="mt-4 small">
          <p className="mb-0">
            Вся история обновлений и модификаций бота @salew_bot и приложения доступна в ТГ канале{' '}
            <a
              href="tg://resolve?domain=salew_app"
              target="_blank"
              rel="noopener noreferrer"
              className="text-decoration-none d-inline-flex align-items-center gap-1"
              style={{ color: '#0d6efd' }}
            >
              @salew_app <ArrowRight />
            </a>
          </p>
        </div>
      </div>

      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        className={theme === 'dark' ? 'text-white modal-dark' : 'text-dark modal-light'}
        centered
      >
        <Modal.Header
          className={theme === 'dark'
            ? 'border-bottom border-secondary bg-dark text-white'
            : 'border-bottom border-light bg-light text-dark'
          }
        >
          <Modal.Title className="fw-bold">Активация бота</Modal.Title>
          <CloseButton
            variant={theme === 'dark' ? 'white' : 'dark'}
            onClick={() => setShowModal(false)}
          />
        </Modal.Header>
        <Modal.Body className={theme === 'dark' ? 'bg-dark text-white' : 'bg-light text-dark'}>
          <div className="d-flex flex-column gap-3">
            <p className="mb-0">
              Для начала работы необходимо активировать нашего бота.
              Это займет всего несколько секунд!
            </p>
            <Button
              href="tg://resolve?domain=salew_bot"
              target="_blank"
              rel="noopener noreferrer"
              variant="primary"
              className="d-flex align-items-center gap-2 justify-content-center fw-bold py-2"
            >
              Перейти к боту
              <ArrowRight size={18} />
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default LoginPage;
